<template>
  <div class="container-fluid px-0">
    <div class="row mb-4 g-3 align-items-center">
      <!-- Gender select -->
      <div :class="wideLayout ? 'col-6 col-md-4' : 'col-6 col-md-2'">
        <select 
          class="form-select form-select-sm" 
          v-model="localFilters.gender" 
          @change="applyFilters"
        >
          <option value="">{{ $t('groupStudentsFilter.allGenders') }}</option>
          <option value="M">{{ $t('groupStudentsFilter.males') }}</option>
          <option value="F">{{ $t('groupStudentsFilter.females') }}</option>
        </select>
      </div>
      
      <!-- National number input -->
      <div :class="wideLayout ? 'col-6 col-md-3' : 'col-6 col-md-2'">
        <input
          type="text"
          class="form-control form-control-sm"
          :placeholder="$t('groupStudentsFilter.nationalNumPlaceholder')"
          v-model="localFilters.national_num"
          @input="handleNationalNumInput"
        />
      </div>
      
      <div class="col-md-1 d-none d-md-block">
        <button 
          class="btn btn-outline-secondary btn-sm w-100" 
          @click="resetFilters"
          :title="$t('groupStudentsFilter.reset')"
        >
          <i class="fas fa-filter-circle-xmark"></i>
        </button>
      </div>
      
      <!-- Search by name -->
      <div :class="wideLayout ? 'col-12 col-md-4' : 'col-12 col-md-5'" class="position-relative">
        <input
          type="text"
          class="form-control form-control-sm ps-5"
          :placeholder="$t('groupStudentsFilter.namePlaceholder')"
          :value="localFilters.search"
          @input="handleSearchInput"
          style="padding-right: 12px;" 
        />
        <span class="position-absolute start-0 top-50 translate-middle-y ps-3">
          <i class="fas fa-search"></i>
        </span>
      </div>
      
      <div class="col-12 d-md-none">
        <button class="btn btn-outline-secondary btn-sm w-100" @click="resetFilters">
          <i class="fas fa-filter-circle-xmark"></i> {{ $t('groupStudentsFilter.reset') }}
        </button>
      </div>
    </div>
  </div>
</template>
  
<script setup>
import { ref, watch, defineProps, defineEmits } from 'vue';

const props = defineProps({
  filters: {
    type: Object,
    required: true,
    default: () => ({
      search: '',
      gender: '',
      national_num: ''
    })
  },
  wideLayout: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:filters', 'apply-filters', 'reset-filters']);

const localFilters = ref({ 
  ...props.filters,
  search: props.filters.search || '',
  gender: props.filters.gender || '',
  national_num: props.filters.national_num || ''
});

watch(
  () => props.filters,
  (newVal) => {
    localFilters.value = { ...newVal };
  },
  { deep: true }
);

const debounce = (fn, delay) => {
  let timeoutId;
  return function(...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), delay);
  };
};

const debouncedSearch = debounce((searchValue) => {
  localFilters.value.search = searchValue;
  applyFilters();
}, 300);

const debouncedNationalNum = debounce((value) => {
  localFilters.value.national_num = value;
  applyFilters();
}, 300);

const handleSearchInput = (event) => {
  debouncedSearch(event.target.value);
};

const handleNationalNumInput = (event) => {
  debouncedNationalNum(event.target.value);
};

const applyFilters = () => {
  emit('update:filters', { ...localFilters.value });
  emit('apply-filters', { ...localFilters.value });
};

const resetFilters = () => {
  localFilters.value = {
    search: '',
    gender: '',
    national_num: ''
  };
  emit('update:filters', { ...localFilters.value });
  emit('reset-filters');
};
</script>